// src/services/communityService.js
import supabase from './supabase';

export const getCommunityByUsername = async (username) => {
  try {
    const { data, error } = await supabase
      .from('communities')
      .select(`
        *,
        members_count:community_members(count),
        events_count:events(count)
      `)
      .eq('username', username)
      .single();

    if (error) throw error;

    // Get actual counts
    data.members_count = data.members_count?.[0]?.count || 0;
    data.events_count = data.events_count?.[0]?.count || 0;

    return data;
  } catch (error) {
    console.error('Error fetching community:', error);
    throw error;
  }
};

export const getCommunityById = async (communityId) => {
  try {
    const { data, error } = await supabase
      .from('communities')
      .select(`
        *,
        members_count:community_members(count),
        events_count:events(count)
      `)
      .eq('id', communityId)
      .single();

    if (error) throw error;

    // Get actual counts
    data.members_count = data.members_count?.[0]?.count || 0;
    data.events_count = data.events_count?.[0]?.count || 0;

    return data;
  } catch (error) {
    console.error('Error fetching community:', error);
    throw error;
  }
};

export const getUserCreatedCommunities = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error} = await supabase
      .from('communities')
      .select(`
        *,
        members_count:community_members(count),
        events_count:events(count)
      `)
      .eq('created_by', user.id)
      .order('created_at', { ascending: false });

    if (error) throw error;

    return data.map(community => ({
      ...community,
      members_count: community.members_count?.[0]?.count || 0,
      events_count: community.events_count?.[0]?.count || 0,
    }));
  } catch (error) {
    console.error('Error fetching created communities:', error);
    throw error;
  }
};

export const getUserJoinedCommunities = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('community_members')
      .select(`
        *,
        community:communities(
          *,
          members_count:community_members(count),
          events_count:events(count)
        )
      `)
      .eq('user_id', user.id)
      .eq('status', 'active')
      .order('joined_at', { ascending: false });

    if (error) throw error;

    return data.map(membership => ({
      ...membership.community,
      members_count: membership.community.members_count?.[0]?.count || 0,
      events_count: membership.community.events_count?.[0]?.count || 0,
    }));
  } catch (error) {
    console.error('Error fetching joined communities:', error);
    throw error;
  }
};

export const getUserMembershipStatus = async (communityId, userId) => {
  try {
    const { data, error } = await supabase
      .from('community_members')
      .select('*')
      .eq('community_id', communityId)
      .eq('user_id', userId)
      .single();

    if (error && error.code !== 'PGRST116') throw error; // PGRST116 = no rows returned
    return data;
  } catch (error) {
    console.error('Error fetching membership status:', error);
    return null;
  }
};

export const getCommunityMembers = async (communityId, filters = {}) => {
  try {
    let query = supabase
      .from('community_members')
      .select(`
        *,
        user:users(
          id,
          username,
          first_name,
          last_name,
          profile_photo_cdn_url
        )
      `)
      .eq('community_id', communityId);

    if (filters.status) {
      query = query.eq('status', filters.status);
    }

    query = query.order('joined_at', { ascending: false });

    const { data, error } = await query;
    if (error) throw error;

    return data;
  } catch (error) {
    console.error('Error fetching community members:', error);
    throw error;
  }
};

export const getCommunityAnnouncements = async (communityId, limit = null) => {
  try {
    let query = supabase
      .from('community_announcements')
      .select(`
        *,
        creator:users(
          id,
          first_name,
          last_name,
          profile_photo_cdn_url
        )
      `)
      .eq('community_id', communityId)
      .order('created_at', { ascending: false });

    if (limit) {
      query = query.limit(limit);
    }

    const { data, error } = await query;
    if (error) throw error;

    return data;
  } catch (error) {
    console.error('Error fetching announcements:', error);
    throw error;
  }
};

export const getCommunityEvents = async (communityId, filters = {}) => {
  try {
    let query = supabase
      .from('events')
      .select('*')
      .eq('community_id', communityId);

    if (filters.status) {
      query = query.eq('status', filters.status);
    }

    // Only show future events by default
    const now = new Date().toISOString();
    query = query.gte('event_date', now);

    query = query.order('event_date', { ascending: true });

    const { data, error } = await query;
    if (error) throw error;

    return data;
  } catch (error) {
    console.error('Error fetching community events:', error);
    throw error;
  }
};

export const requestToJoinCommunity = async (communityId, requestData = {}) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    // Check if community exists
    const { data: community, error: communityError } = await supabase
      .from('communities')
      .select('*')
      .eq('id', communityId)
      .single();

    if (communityError) throw communityError;

    // For approval-required communities, create an access request
    if (community.privacy === 'approval') {
      const { data, error } = await supabase
        .from('community_access_requests')
        .insert({
          community_id: communityId,
          user_id: user.id,
          message: requestData.message || '',
          answers: requestData.answers || {},
          status: 'pending',
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    }

    // For public/private communities, directly create membership
    const { data, error } = await supabase
      .from('community_members')
      .insert({
        community_id: communityId,
        user_id: user.id,
        status: 'active',
        role: 'member',
        membership_type: community.membership_enabled ? 'paid' : 'free',
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error joining community:', error);
    throw error;
  }
};

export const leaveCommunity = async (communityId) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase
      .from('community_members')
      .delete()
      .eq('community_id', communityId)
      .eq('user_id', user.id);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error leaving community:', error);
    throw error;
  }
};

export const checkActiveMembership = async (communityId, userId) => {
  try {
    const { data, error } = await supabase
      .from('community_members')
      .select('*')
      .eq('community_id', communityId)
      .eq('user_id', userId)
      .eq('status', 'active')
      .single();

    if (error && error.code !== 'PGRST116') throw error;
    return !!data;
  } catch (error) {
    console.error('Error checking membership:', error);
    return false;
  }
};

export const getPendingAccessRequests = async (communityId) => {
  try {
    const { data, error } = await supabase
      .from('community_access_requests')
      .select(`
        *,
        user:users(
          id,
          username,
          first_name,
          last_name,
          profile_photo_cdn_url
        )
      `)
      .eq('community_id', communityId)
      .eq('status', 'pending')
      .order('created_at', { ascending: false});

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error fetching pending requests:', error);
    throw error;
  }
};

export const getUserAccessRequestStatus = async (communityId, userId) => {
  try {
    const { data, error } = await supabase
      .from('community_access_requests')
      .select('*')
      .eq('community_id', communityId)
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(1)
      .single();

    if (error && error.code !== 'PGRST116') throw error;
    return data;
  } catch (error) {
    console.error('Error fetching request status:', error);
    return null;
  }
};

export const approveAccessRequest = async (requestId) => {
  try {
    // Get the request details
    const { data: request, error: requestError } = await supabase
      .from('community_access_requests')
      .select('*')
      .eq('id', requestId)
      .single();

    if (requestError) throw requestError;

    // Update request status
    const { error: updateError } = await supabase
      .from('community_access_requests')
      .update({ status: 'approved' })
      .eq('id', requestId);

    if (updateError) throw updateError;

    // Create membership
    const { error: memberError } = await supabase
      .from('community_members')
      .insert({
        community_id: request.community_id,
        user_id: request.user_id,
        status: 'active',
        role: 'member',
      });

    if (memberError) throw memberError;
    return true;
  } catch (error) {
    console.error('Error approving request:', error);
    throw error;
  }
};

export const rejectAccessRequest = async (requestId) => {
  try {
    const { error } = await supabase
      .from('community_access_requests')
      .update({ status: 'rejected' })
      .eq('id', requestId);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error rejecting request:', error);
    throw error;
  }
};
